/**
 * Assessment Creation Form Component
 * Reference implementation following UI Standards (see /UI_STANDARDS.md)
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert, CircularProgress,
  Breadcrumbs, Link as MuiLink, Stack, FormControl, InputLabel, Select, MenuItem,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText, Checkbox, Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AssessmentCreate } from '../../types/assessment';
import { Subject } from '../../types/subject';
import { GRADE_LEVELS } from '../../types/common';

const AssessmentForm: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<AssessmentCreate>({
    title: '',
    description: '',
    due_date: '',
    max_points: 0,
    class_id: 0,
    teacher_id: 0,
    rubric_id: null,
    grade_level: null,
    subject: null,
    submission_format: 'file',
    assigned_class_ids: [],
  });

  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  // Dropdown data
  const [classes, setClasses] = useState<any[]>([]);
  const [teachers, setTeachers] = useState<any[]>([]);
  const [rubrics, setRubrics] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  // Load dropdown data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [classesRes, teachersRes, rubricsRes, subjectsRes] = await Promise.all([
          axios.get('/api/school-classes/'),
          axios.get('/api/teachers/'),
          axios.get('/api/rubrics/'),
          axios.get('/api/subjects/?ordering=name&page_size=1000'),
        ]);

        setClasses(classesRes.data.results || []);
        setTeachers(teachersRes.data.results || []);
        setRubrics(rubricsRes.data.results || []);
        setSubjects(subjectsRes.data.results || []);
      } catch (err: any) {
        console.error('Error loading dropdown data:', err);
        setError('Failed to load form data. Please refresh the page.');
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, []);

  const validate = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.title || formData.title.trim() === '') {
      errors.title = 'Title is required';
    }

    if (!formData.due_date || formData.due_date.trim() === '') {
      errors.due_date = 'Due date is required';
    }

    if (!formData.max_points || formData.max_points === 0) {
      errors.max_points = 'Maximum points is required';
    } else if (formData.max_points < 0) {
      errors.max_points = 'Maximum points must be positive';
    }

    if (!formData.assigned_class_ids || formData.assigned_class_ids.length === 0) {
      errors.assigned_class_ids = 'Please select at least one class';
    }

    if (!formData.teacher_id || formData.teacher_id === 0) {
      errors.teacher_id = 'Teacher ID is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? 0 : Number(value)) : value
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSelectChange = (name: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? null : value
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleClassToggle = (classId: number) => {
    setFormData(prev => {
      const currentIds = prev.assigned_class_ids || [];
      const newIds = currentIds.includes(classId)
        ? currentIds.filter(id => id !== classId)
        : [...currentIds, classId];
      return {
        ...prev,
        assigned_class_ids: newIds,
        // Also update class_id to the first selected class for backward compatibility
        class_id: newIds.length > 0 ? newIds[0] : 0
      };
    });

    // Clear validation error when user selects a class
    if (validationErrors.assigned_class_ids) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.assigned_class_ids;
        return newErrors;
      });
    }
  };

  // Filter classes by grade level and subject if they are set
  const filteredClasses = classes.filter(cls => {
    if (formData.grade_level && cls.grade_level !== formData.grade_level) return false;
    if (formData.subject && cls.subject_name !== formData.subject) return false;
    return true;
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validate()) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    setLoading(true);

    try {
      // Exclude teacher_id from payload - backend will set created_by from authenticated user
      const { teacher_id, class_id, ...payloadData } = formData;
      const payload = {
        ...payloadData,
        description: formData.description || null,
        rubric_id: formData.rubric_id || null,
        assigned_class_ids: formData.assigned_class_ids,
      };

      await axios.post('/api/assignments/', payload);
      setSuccess(true);

      setTimeout(() => {
        navigate('/assessments');
      }, 1500);
    } catch (err: any) {
      console.error('Error creating assessment:', err);

      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          setError(err.response.data.detail);
        } else if (Array.isArray(err.response.data.detail)) {
          const errorMessages = err.response.data.detail
            .map((e: any) => `${e.loc.join('.')}: ${e.msg}`)
            .join(', ');
          setError(errorMessages);
        } else {
          setError('Failed to create assessment. Please check your input.');
        }
      } else {
        setError('Failed to create assessment. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/assessments');
  };

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink
          component="button"
          variant="body1"
          onClick={() => navigate('/assessments')}
          sx={{ cursor: 'pointer', textDecoration: 'none' }}
        >
          Assessments
        </MuiLink>
        <Typography color="text.primary">New Assessment</Typography>
      </Breadcrumbs>

      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Create New Assessment
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Enter the details for the new assessment
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Assessment created successfully! Redirecting to assessments list...
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              required
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              error={!!validationErrors.title}
              helperText={validationErrors.title}
              disabled={loading}
            />

            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description || ''}
              onChange={handleChange}
              disabled={loading}
              multiline
              rows={3}
              helperText="Optional"
            />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Grade Level</InputLabel>
                <Select
                  value={formData.grade_level || ''}
                  onChange={(e) => handleSelectChange('grade_level', e.target.value)}
                  label="Grade Level"
                  disabled={loading}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {GRADE_LEVELS.map((grade) => (
                    <MenuItem key={grade.value} value={grade.value}>
                      {grade.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Subject</InputLabel>
                <Select
                  value={formData.subject || ''}
                  onChange={(e) => handleSelectChange('subject', e.target.value)}
                  label="Subject"
                  disabled={loading}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {subjects.map((subj) => (
                    <MenuItem key={subj.id} value={subj.name}>
                      {subj.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                required
                type="datetime-local"
                label="Due Date"
                name="due_date"
                value={formData.due_date}
                onChange={handleChange}
                error={!!validationErrors.due_date}
                helperText={validationErrors.due_date}
                disabled={loading}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                required
                type="number"
                label="Maximum Points"
                name="max_points"
                value={formData.max_points || ''}
                onChange={handleChange}
                error={!!validationErrors.max_points}
                helperText={validationErrors.max_points}
                disabled={loading}
                inputProps={{ min: 0, step: 0.5 }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl fullWidth required error={!!validationErrors.teacher_id}>
                <InputLabel>Teacher</InputLabel>
                <Select
                  value={formData.teacher_id || ''}
                  onChange={(e) => handleSelectChange('teacher_id', e.target.value)}
                  label="Teacher"
                  disabled={loading || loadingData}
                >
                  <MenuItem value="">
                    <em>Select a teacher...</em>
                  </MenuItem>
                  {teachers.map((teacher) => (
                    <MenuItem key={teacher.id} value={teacher.id}>
                      {teacher.first_name} {teacher.last_name} ({teacher.email})
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.teacher_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {validationErrors.teacher_id}
                  </Typography>
                )}
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Rubric</InputLabel>
                <Select
                  value={formData.rubric_id || ''}
                  onChange={(e) => handleSelectChange('rubric_id', e.target.value)}
                  label="Rubric"
                  disabled={loading || loadingData}
                >
                  <MenuItem value="">
                    <em>None (Optional)</em>
                  </MenuItem>
                  {rubrics.map((rubric) => (
                    <MenuItem key={rubric.id} value={rubric.id}>
                      {rubric.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Assign to Classes {formData.assigned_class_ids && formData.assigned_class_ids.length > 0 &&
                  `(${formData.assigned_class_ids.length} selected)`}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Select one or more classes to assign this assessment to. All selected classes will share the same assignment, title, due date, and rubric.
              </Typography>

              {validationErrors.assigned_class_ids && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {validationErrors.assigned_class_ids}
                </Alert>
              )}

              {loadingData ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : filteredClasses.length === 0 ? (
                <Alert severity="info">
                  No classes match the selected grade level and subject. Adjust the filters above to see available classes.
                </Alert>
              ) : (
                <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                  <List dense>
                    {filteredClasses.map((cls, index) => {
                      const isChecked = (formData.assigned_class_ids || []).includes(cls.id);
                      return (
                        <React.Fragment key={cls.id}>
                          {index > 0 && <Divider />}
                          <ListItem disablePadding>
                            <ListItemButton onClick={() => handleClassToggle(cls.id)} disabled={loading}>
                              <ListItemIcon>
                                <Checkbox
                                  edge="start"
                                  checked={isChecked}
                                  tabIndex={-1}
                                  disableRipple
                                  disabled={loading}
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={cls.name}
                                secondary={`Grade ${cls.grade_level} — ${cls.subject_name || '(no subject)'}`}
                              />
                            </ListItemButton>
                          </ListItem>
                        </React.Fragment>
                      );
                    })}
                  </List>
                </Paper>
              )}
            </Box>

            <FormControl fullWidth>
              <InputLabel>Submission Format</InputLabel>
              <Select
                value={formData.submission_format || 'file'}
                onChange={(e) => handleSelectChange('submission_format', e.target.value)}
                label="Submission Format"
                disabled={loading}
              >
                <MenuItem value="file">File Upload</MenuItem>
                <MenuItem value="text">Text/Code Paste</MenuItem>
                <MenuItem value="file_text">File and Text</MenuItem>
              </Select>
            </FormControl>

            <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
              <Button variant="outlined" onClick={handleCancel} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Creating...' : 'Create Assessment'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default AssessmentForm;
